import React, {Fragment} from "react";
import DocumentSection from "../manageTemplates/DocumentSection";

import {
  generalTabLocationFields,
  generalTabListingManagementFields,
  generalTabBusinessAddressFields
} from "./general/BulkLocationEditorGeneralDetails";
import * as mobx from "mobx";

import _, {transform, isEqual, isObject, differenceWith} from 'lodash'
import Button from "../../../components/buttons/Button";

/**
 * Deep diff between two object, using lodash
 * @param  {Object} object Object compared
 * @param  {Object} base   Object to compare with
 * @return {Object}        Return a new object who represent the diff
 */
function difference(object, base) {
  function changes(object, base) {
    return transform(object, function (result, value, key) {
      if (!isEqual(value, base[key])) {
        result[key] = (isObject(value) && isObject(base[key])) ? changes(value, base[key]) : value;
      }
    });
  }

  return changes(object, base);
}

function photoArrayDiffs(x, y) {
  return differenceWith(x, y, _.isEqual)
}

const isOptionalArrayFieldEmpty = (optionalArrayField) => {
  return (optionalArrayField === undefined || optionalArrayField == null || (Array.isArray(optionalArrayField) && optionalArrayField.length <= 0))
}

function BulkLocationEditorSummary({
  setFormIsInvalid,
  combinedLocationServicesMap,
  singleLocationMode,
  locationPatch,
  startingLocationPatch,
  setActiveTab,
  accountsStore,
  yelpCategoriesStore,
  bingCategoriesStore,
  appleCategoriesStore,
  googleAttributesStore,
  hideEditButtons,
  foodMenuSummary,
  createMode
}) {

  const diff = !!startingLocationPatch ? difference(locationPatch, startingLocationPatch) : locationPatch;

  const patch = diff;


  const hoursPatch = !!patch.listingManagementSpecs && !!patch.listingManagementSpecs.hours ? locationPatch.listingManagementSpecs.hours : [];
  const specialHoursPatch = !!patch.listingManagementSpecs && !!patch.listingManagementSpecs.specialHours ? locationPatch.listingManagementSpecs.specialHours : [];
  const temporarilyClosedPatch = patch.listingManagementSpecs && patch.listingManagementSpecs.temporarilyClosedUntil;

  const hasGeneralTabListingDetailsChanges = patch.accountId !== undefined ||
    generalTabLocationFields.findIndex(field => (!!patch[field] || patch[field] === "")) !== -1 ||
    generalTabListingManagementFields.findIndex(field => (!!(patch.listingManagementSpecs || {})[field] || (patch.listingManagementSpecs || {})[field] === "")) !== -1 ||
    generalTabBusinessAddressFields.findIndex(field => (!!(patch.businessAddress || {})[field] || (patch.businessAddress || {})[field] === "")) !== -1 ||
    (!!patch.listingManagementSpecs && patch.listingManagementSpecs.paymentTypes === null) || // payment types is a special case because it is nullable
    (Array.isArray(patch.alternateWebsites)) || // alternate websites is a special case because it is an array
    (Array.isArray(patch.alternatePhoneNumbers)); // alternate phone numbers is a special case because it is an array

  const appointmentUrl = !!patch.listingManagementSpecs && patch.listingManagementSpecs.appointmentUrl;

  const hasGeneralTabLocationTypeStatusChanges = !(patch.locationType === null || patch.locationType === undefined)
    || (!!patch.businessAddress && !(patch.businessAddress.isServiceAreaLocation === null || patch.businessAddress.isServiceAreaLocation === undefined))
    || (!!patch.listingManagementSpecs && !(patch.listingManagementSpecs.hideAddress === null || patch.listingManagementSpecs.hideAddress === undefined))
    || (!!patch.listingManagementSpecs && !(patch.listingManagementSpecs.permanentlyClosed === null || patch.listingManagementSpecs.permanentlyClosed === undefined))

  const hasGeneralTabInternalUseOnlyChanges = (patch.hasOwnProperty("emails"))
    || (patch.clientLocationId !== undefined)
    || (patch.brandMonitoring && patch.brandMonitoring.listingMonitoring[0] !== undefined)
    || patch.notes !== undefined;


  const accounts = mobx.toJS(accountsStore.accounts);
  const maybeAccount = !!patch.accountId && accounts.find(account => account.id === patch.accountId);
  const maybeSelectedAccountName = maybeAccount && maybeAccount.accountName;

  const allYelpCategories = mobx.toJS(yelpCategoriesStore.categories);
  const selectedYelpCategories = !!patch.yelpListingManagementSpecs && !!patch.yelpListingManagementSpecs.categories && locationPatch.yelpListingManagementSpecs.categories.map(yelpCategoryAlias => allYelpCategories.find(yelpCategory => yelpCategory.alias === yelpCategoryAlias).title);

  const googleCategories = !!patch.googleListingManagementSpecs && !!patch.googleListingManagementSpecs.googleCategories && locationPatch.googleListingManagementSpecs.googleCategories;
  const googleTrackingUrl = !!patch.listingManagementSpecs && (patch.listingManagementSpecs.providerTrackingWebsiteUrls || {}).hasOwnProperty("GOOGLEMAP")
  const googleManuallyPlacedPin = !!patch.googleListingManagementSpecs && patch.googleListingManagementSpecs.doNotMovePin !== undefined;
  // We set appointment url via the location's appointment url field, not with google attributes
  const googleAttributes = (!!patch.googleListingManagementSpecs && !!patch.googleListingManagementSpecs.attributes) ? locationPatch.googleListingManagementSpecs.attributes.filter(attr => attr.attributeId !== "url_appointment") : [];

  const haveLocGooglePhotos = (locationPatch.googleListingManagementSpecs || {}) && (locationPatch.googleListingManagementSpecs.photos || [])
  const haveStartingLocGooglePhotos = (startingLocationPatch.googleListingManagementSpecs || {}) && (startingLocationPatch.googleListingManagementSpecs.photos || [])
  const locGooglePhotos = haveLocGooglePhotos ? locationPatch.googleListingManagementSpecs.photos : []
  const startingLocGooglePhotos = haveStartingLocGooglePhotos ? startingLocationPatch.googleListingManagementSpecs.photos : []
  const maybeAddedGooglePhotos = !!patch.googleListingManagementSpecs && !!patch.googleListingManagementSpecs.photos ? patch.googleListingManagementSpecs.photos : []
  const googleAdditionalPhotos = haveLocGooglePhotos ? photoArrayDiffs(locGooglePhotos, startingLocGooglePhotos).filter(gPhoto => gPhoto.locationAssociationCategory == "ADDITIONAL") : []
  //In the case where we've removed an image, we have to identify which one was removed, and our `difference` function above is able to identify there is a difference, but not which one. The below code finds that difference.
  const googleRemovedPhotos = haveStartingLocGooglePhotos ? photoArrayDiffs(startingLocGooglePhotos, locGooglePhotos) : []
  const googleProfilePhotos = haveStartingLocGooglePhotos ? photoArrayDiffs(locGooglePhotos, startingLocGooglePhotos).filter(gPhoto => gPhoto.locationAssociationCategory == "PROFILE") : []
  const googleCoverPhotos = haveStartingLocGooglePhotos ? photoArrayDiffs(locGooglePhotos, startingLocGooglePhotos).filter(gPhoto => gPhoto.locationAssociationCategory == "COVER") : []

  const hasLocalPagesUpdate = (locationPatch.localPages)
  const allBingCategories = mobx.toJS(bingCategoriesStore.categories);
  const selectedBingCategories = !!patch.listingManagementSpecs && !!patch.listingManagementSpecs.bingCategories ? (locationPatch.listingManagementSpecs.bingCategories
    .map(categoryName => allBingCategories.find(bingCategory => bingCategory.text.toLowerCase() === categoryName.toLowerCase()))
    .filter(maybeCategory => maybeCategory !== undefined)
    .map(category => category.text)) : [];


  const maybePrimaryBingCategory = !!patch.listingManagementSpecs && !!patch.listingManagementSpecs.primaryBingCategory && allBingCategories.find(bingCategory => bingCategory.text.toLowerCase() === patch.listingManagementSpecs.primaryBingCategory.toLowerCase())
  const allAppleCategories = mobx.toJS(appleCategoriesStore.categories);
  const selectedAppleCategories = !!patch.listingManagementSpecs && !!patch.listingManagementSpecs.appleCategories ? (locationPatch.listingManagementSpecs.appleCategories
    .map(categoryId => allAppleCategories.find(appleCategory => appleCategory.categoryId === categoryId))
    .filter(maybeCategory => maybeCategory !== undefined)
    .map(category => category.text)) : [];

  const hasHealthcareChanges = !!patch.listingManagementSpecs && !!patch.listingManagementSpecs.healthCareManagement
    && (!!patch.listingManagementSpecs.healthCareManagement.nationalProviderIdentifier
      || !!patch.listingManagementSpecs.healthCareManagement.specialties
      || !!patch.listingManagementSpecs.healthCareManagement.conditionsTreated
      || patch.listingManagementSpecs.healthCareManagement.acceptingNewPatients === true
      || patch.listingManagementSpecs.healthCareManagement.acceptingNewPatients === false);

  const maybePrimaryAppleCategory = !!patch.listingManagementSpecs && !!patch.listingManagementSpecs.primaryAppleCategory && allAppleCategories.find(appleCategory => appleCategory.categoryId === patch.listingManagementSpecs.primaryAppleCategory)

  const keywords = !!patch.additionalCategories ? locationPatch.additionalCategories : [];

  const hasRequiredPrimaryBingCategory = !singleLocationMode || !combinedLocationServicesMap.bingListingManagement || !!locationPatch.listingManagementSpecs.primaryBingCategory;
  const hasRequiredHours = !singleLocationMode || !combinedLocationServicesMap.listingManagement || locationPatch.listingManagementSpecs.hours.length === 7;

  setFormIsInvalid(!hasRequiredPrimaryBingCategory || !hasRequiredHours);

  const attributeMods = (cmAtt) => {
    const displayName = googleAttributesStore.cmAttributeToGoogleAttribute(cmAtt).displayName
    let vals = "";
    if (cmAtt.valueType === "BOOL" || cmAtt.valueType === "ENUM") {
      vals = cmAtt.values
    } else if (cmAtt.valueType === "URL"){
      vals = cmAtt.urlValues.map(obj=> obj.url).join(", ")
    }  else if (cmAtt.valueType === "REPEATED_ENUM"){
      vals = cmAtt.repeatedEnumValue.setValues.join(", ")
    }

    return displayName + ": " + vals
  }

  return <div>
    {hasLocalPagesUpdate && <div>
      <h5 className="card-spacer-top"> Local Pages {!hideEditButtons &&
      <button className="btn btn-outline-secondary" onClick={() => setActiveTab("Local Pages")}><i
          className="fas fa-edit"/> Edit</button>}</h5>
      {locationPatch.localPages.localPagesLocationDocument.templateFieldsAndSections.map(section => {
        return <div className="col-12 col-sm-12" key={"div-key-"+section.sectionId}>
          <DocumentSection
              section={section}
              readOnly={true}
              key={"document-key-"+section.sectionId}
          />
        </div>
      })}
      {!!locationPatch.localPages.localPagesLocationDocument.hasOwnProperty("publishedStatus") &&<div>
        {"Publish: " +locationPatch.localPages.localPagesLocationDocument.publishedStatus}
      </div>}
      {!!locationPatch.localPages.localPagesLocationDocument.hasOwnProperty("publishedUrl") &&<div>
        {"Live URL: " +locationPatch.localPages.localPagesLocationDocument.publishedUrl}
      </div>}
    </div>
    }
    {hasGeneralTabListingDetailsChanges && <div>
      <div>
      {!hideEditButtons && <div className="card-padding pb-0 d-flex justify-content-end"><Button icon="fas fa-edit" className="ml-auto" onClick={() => setActiveTab("General")}>Edit</Button></div>}
      <table className="table table-striped table-hover table-borderless card-spacer-top">
        <tbody>
        {!!patch.accountId && <tr>
          <td>{!createMode && "Move Location(s) to "}Account</td>
          <td>{maybeSelectedAccountName || patch.accountId}</td>
        </tr>}
        {!!patch.businessName && <tr>
          <td>Business Name</td>
          <td>{patch.businessName}</td>
        </tr>}
        {(!!patch.primaryWebsite || patch.primaryWebsite === "") && <tr>
          <td>Primary Website</td>
          <td>{patch.primaryWebsite === "" ? "Deleted" : patch.primaryWebsite}</td>
        </tr>}
        {(patch.alternateWebsites || []).map(website => <tr>
          <td>Alternate Website</td>
          <td>{website}</td>
        </tr>)}
        {Array.isArray(patch.alternateWebsites) && patch.alternateWebsites.length === 0 && <tr>
          <td>Alternate Website</td>
          <td> Deleted</td>
        </tr>}
        {!!patch.primaryPhoneNumber && <tr>
          <td>Main Phone</td>
          <td>{patch.primaryPhoneNumber}</td>
        </tr>}
        {(patch.alternatePhoneNumbers || []).map(phoneNumbers => <tr>
          <td>Alternate Phone Number</td>
          <td>{phoneNumbers}</td>
        </tr>)}
        {Array.isArray(patch.alternatePhoneNumbers) && patch.alternatePhoneNumbers.length === 0 && <tr>
          <td>Alternate Phone Number</td>
          <td> Deleted</td>
        </tr>}
        {!!patch.primaryCategory && <tr>
          <td>Primary Category</td>
          <td>{patch.primaryCategory}</td>
        </tr>}
        {!!patch.businessAddress && !!patch.businessAddress.country && <tr>
          <td>Country</td>
          <td>{patch.businessAddress.country}</td>
        </tr>}
        {!!patch.businessAddress && !!patch.businessAddress.state && <tr>
          <td>State/Province</td>
          <td>{patch.businessAddress.state}</td>
        </tr>}
        {!!patch.businessAddress && !!patch.businessAddress.city && <tr>
          <td>City</td>
          <td>{patch.businessAddress.city}</td>
        </tr>}
        {!!patch.businessAddress && !!patch.businessAddress.postalCode && <tr>
          <td>Postal Code</td>
          <td>{patch.businessAddress.postalCode}</td>
        </tr>}
        {!!patch.businessAddress && !!patch.businessAddress.street && <tr>
          <td>Street</td>
          <td>{patch.businessAddress.street}</td>
        </tr>}
        {!!patch.businessAddress && (!!patch.businessAddress.suite || patch.businessAddress.suite === "") && <tr>
          <td>Suite</td>
          <td>{patch.businessAddress.suite === "" ? "Deleted" : patch.businessAddress.suite}</td>
        </tr>}
        {!!patch.listingManagementSpecs && !!patch.listingManagementSpecs.description && <tr>
          <td className="apply-vertical-align-top apply-whitespace-no-wrap">Business Description</td>
          <td className="apply-word-break-all">{patch.listingManagementSpecs.description}</td>
        </tr>}
        {!!patch.listingManagementSpecs && (!!patch.listingManagementSpecs.businessOwner || patch.listingManagementSpecs.businessOwner === "") &&
        <tr>
          <td>Business Owner</td>
          <td>{patch.listingManagementSpecs.businessOwner === "" ? "Deleted" : patch.listingManagementSpecs.businessOwner}</td>
        </tr>}
        {!!patch.listingManagementSpecs && (!!patch.listingManagementSpecs.businessEmail || patch.listingManagementSpecs.businessEmail === "") &&
        <tr>
          <td>Business Email</td>
          <td>{patch.listingManagementSpecs.businessEmail === "" ? "Deleted" : patch.listingManagementSpecs.businessEmail}</td>
        </tr>}
        {!!patch.listingManagementSpecs && (!!patch.listingManagementSpecs.faxNumber || patch.listingManagementSpecs.faxNumber === "") &&
        <tr>
          <td>Business Fax</td>
          <td>{patch.listingManagementSpecs.faxNumber === "" ? "Deleted" : patch.listingManagementSpecs.faxNumber}</td>
        </tr>}
        {!!patch.listingManagementSpecs && (!!patch.listingManagementSpecs.yearEstablished || patch.listingManagementSpecs.yearEstablished === "") &&
        <tr>
          <td>Year Established</td>
          <td>{patch.listingManagementSpecs.yearEstablished === "" ? "Deleted" : patch.listingManagementSpecs.yearEstablished}</td>
        </tr>}
        {!!patch.listingManagementSpecs && patch.listingManagementSpecs.paymentTypes !== undefined && <tr>
          <td>Payment Types</td>
          <td>{patch.listingManagementSpecs.paymentTypes === null ? "Deleted" : patch.listingManagementSpecs.paymentTypes}</td>
        </tr>}
        </tbody>
      </table>

      </div>    
    </div>}
    {hasGeneralTabLocationTypeStatusChanges && <div>
      <h5 className="card-spacer-top">Location Type/Status {!hideEditButtons &&
      <button className="btn btn-outline-secondary" onClick={() => setActiveTab("General")}><i
        className="fas fa-edit"/> Edit</button>}</h5>
      <table className="card-spacer-top">
        <tbody>
        {patch.locationType === "customer" && <tr>
          <td>Audit Only</td>
          <td>No</td>
        </tr>}
        {patch.locationType === "audit" && <tr>
          <td>Audit Only</td>
          <td>Yes</td>
        </tr>}
        {!!patch.businessAddress && patch.businessAddress.isServiceAreaLocation === true && <tr>
          <td>Service Area Business</td>
          <td>Yes</td>
        </tr>}
        {!!patch.businessAddress && patch.businessAddress.isServiceAreaLocation === false && <tr>
          <td>Service Area Business</td>
          <td>No</td>
        </tr>}
        {!!patch.listingManagementSpecs && patch.listingManagementSpecs.hideAddress === true && <tr>
          <td>Hide Address</td>
          <td>Yes</td>
        </tr>}
        {!!patch.listingManagementSpecs && patch.listingManagementSpecs.hideAddress === false && <tr>
          <td>Hide Address</td>
          <td>No</td>
        </tr>}
        {!!patch.listingManagementSpecs && patch.listingManagementSpecs.permanentlyClosed === true && <tr>
          <td>Permanently Closed</td>
          <td>Yes</td>
        </tr>}
        {!!patch.listingManagementSpecs && patch.listingManagementSpecs.permanentlyClosed === false && <tr>
          <td>Permanently Closed</td>
          <td>No</td>
        </tr>}
        </tbody>
      </table>
    </div>}
    {hasGeneralTabInternalUseOnlyChanges && <div>
      <h5 className="card-spacer-top">Internal Use Only {!hideEditButtons &&
      <button className="btn btn-outline-secondary" onClick={() => setActiveTab("General")}><i
        className="fas fa-edit"/> Edit</button>}</h5>
      <table className="card-spacer-top">
        <tbody>
        {(!!patch.clientLocationId || patch.clientLocationId === "") && <tr>
          <td>Client Location ID</td>
          <td>{patch.clientLocationId === "" ? "Deleted" : patch.clientLocationId}</td>
        </tr>}
        {(patch.emails || []).map(email => <tr>
          <td>Add Alert Email Address</td>
          <td>{email}</td>
        </tr>)}
        {Array.isArray(patch.emails) && patch.emails.length === 0 && <tr>
          <td>Remove Alert Email Address</td>
          <td/>
        </tr>}
        {(!!patch.notes || patch.notes === "") && <tr>
          <td>Notes</td>
          <td>{patch.notes === "" ? "Deleted" : patch.notes}</td>
        </tr>}
        {!!patch.brandMonitoring && patch.brandMonitoring.listingMonitoring[0] !== undefined && <tr>
          <td>Directory Name Search</td>
          <td>{patch.brandMonitoring.listingMonitoring[0].name === "" ? "Deleted" : patch.brandMonitoring.listingMonitoring[0].name}</td>
        </tr>}
        </tbody>
      </table>
    </div>}
    {hasHealthcareChanges && <div>
      <h5 className="card-spacer-top">Healthcare Information {!hideEditButtons &&
      <button className="btn btn-outline-secondary" onClick={() => setActiveTab("General")}><i
        className="fas fa-edit"/> Edit</button>}</h5>
      <table className="card-spacer-top">
        <tbody>
        {!!patch.listingManagementSpecs && patch.listingManagementSpecs.healthCareManagement && patch.listingManagementSpecs.healthCareManagement.nationalProviderIdentifier &&
        <tr>
          <td>National Provider Identifier</td>
          <td>{patch.listingManagementSpecs.healthCareManagement.nationalProviderIdentifier}</td>
        </tr>}
        {!!patch.listingManagementSpecs && patch.listingManagementSpecs.healthCareManagement && !!patch.listingManagementSpecs.healthCareManagement.specialties &&
        <tr>
          <td>Specialties</td>
          <td>{patch.listingManagementSpecs.healthCareManagement.specialties}</td>
        </tr>}
        {!!patch.listingManagementSpecs && patch.listingManagementSpecs.healthCareManagement && !!patch.listingManagementSpecs.healthCareManagement.conditionsTreated &&
        <tr>
          <td>Conditions Treated</td>
          <td>{patch.listingManagementSpecs.healthCareManagement.conditionsTreated}</td>
        </tr>}
        {!!patch.listingManagementSpecs && patch.listingManagementSpecs.healthCareManagement && patch.listingManagementSpecs.healthCareManagement.acceptingNewPatients === true &&
        <tr>
          <td>Accepting New Patients</td>
          <td>Yes</td>
        </tr>}
        {!!patch.listingManagementSpecs && patch.listingManagementSpecs.healthCareManagement && patch.listingManagementSpecs.healthCareManagement.acceptingNewPatients === false &&
        <tr>
          <td>Accepting New Patients</td>
          <td>No</td>
        </tr>}
        </tbody>
      </table>
    </div>}
    {!!hoursPatch && hoursPatch.length > 0 && <div>
      <h5 className="card-spacer-top">Hours {!hideEditButtons &&
      <button className="btn btn-outline-secondary content-spacer-top" onClick={() => setActiveTab("Hours")}><i
        className="fas fa-edit"/> Edit</button>}</h5>
      {hoursPatch.map((day, index) => {
        return <div key={index}>
          <b>{day.name}:{" "}</b>
          {!day.hasNone && !day.open24Hours && !day.isClosed && day.hourBlocks.map((hourBlock, hourIndex) => {
            return <Fragment><span key={index + "" + hourIndex}>
              Open from {moment(hourBlock.openTime, 'HH:mm').format('hh:mm a')} to {moment(hourBlock.closeTime, 'HH:mm').format('hh:mm a')}
            </span><br/></Fragment>
          })}
          {day.open24Hours && <span>Open 24 Hours</span>}
          {day.isClosed && <span>Closed</span>}
          {day.hasNone && <span>None</span>}
        </div>
      })}
    </div>}
    {!hasRequiredHours && <div>
      <h5 className="card-spacer-top">Hours {!hideEditButtons &&
      <button className="btn btn-outline-secondary" onClick={() => setActiveTab("Hours")}><i
        className="fas fa-edit"/> Edit</button>}</h5>
      <div className="invalid-feedback card-spacer-top" style={{"display": "block"}}>Listing Management locations must set hours.</div>
    </div>}
    {(!!temporarilyClosedPatch || temporarilyClosedPatch === null) && <div>
      <h5 className="card-spacer-top">Temporarily Closed {!hideEditButtons &&
      <button className="btn btn-outline-secondary" onClick={() => setActiveTab("Hours")}><i
        className="fas fa-edit"/> Edit</button>}</h5>
      {temporarilyClosedPatch === null ? <div>Reopen temporarily closed locations</div> :
        <div>Re-opening Date: {temporarilyClosedPatch}</div>}
    </div>}
    {!!specialHoursPatch && specialHoursPatch.length > 0 && <div>
      <h5 className="card-spacer-top">Special Hours {!hideEditButtons &&
      <button className="btn btn-outline-secondary" onClick={() => setActiveTab("Hours")}><i
        className="fas fa-edit"/> Edit</button>}</h5>
      {specialHoursPatch.map((specialHourPeriod, index) => {
        return <div key={index}>
          <b>{specialHourPeriod.date}:</b>
          {!specialHourPeriod.open24Hours && !specialHourPeriod.isClosed &&
          <span>Open from {moment(specialHourPeriod.openTime, 'HH:mm').format('hh:mm a')} to {moment(specialHourPeriod.closeTime, 'HH:mm').format('hh:mm a')}</span>}
          {specialHourPeriod.open24Hours && <span>Open 24 Hours</span>}
          {specialHourPeriod.isClosed && <span>Closed</span>}
        </div>
      })}
    </div>}
    {!!patch.listingManagementSpecs && !!patch.listingManagementSpecs.logoUrl && <div>
      <h5 className="card-spacer-top">Set Logo {!hideEditButtons &&
      <button className="btn btn-outline-secondary" onClick={() => setActiveTab("Images")}><i
        className="fas fa-edit"/> Edit</button>}</h5>
      <div className="row card-spacer-top">
        <div className="col-4 col-sm-4">
          <div className="img-gallery-item">
            <div className="text-center overflow-hidden">
              <img src={patch.listingManagementSpecs.logoUrl} className="content-spacer-top"
                   style={{"height": "100px"}}/>
            </div>
          </div>
        </div>
      </div>
    </div>}
    {(!!patch.listingManagementSpecs && Array.isArray(patch.listingManagementSpecs.imageUrls) && patch.listingManagementSpecs.imageUrls.length > 0) &&
    <div>
      <h5 className="card-spacer-top">Add General Images {!hideEditButtons &&
      <button className="btn btn-outline-secondary" onClick={() => setActiveTab("Images")}><i
        className="fas fa-edit"/> Edit</button>}</h5>
      <div className="row card-spacer-top">
        {patch.listingManagementSpecs.imageUrls.map(imageUrl => {
          return <div className="col-4 col-sm-4">
            <div className="img-gallery-item">
              <div className="text-center overflow-hidden">
                <img src={imageUrl} className="content-spacer-top" style={{"height": "100px"}}/>
              </div>
            </div>
          </div>
        })}
      </div>
    </div>}
    {(selectedYelpCategories.length > 0) && <div>
      <h5 className="card-spacer-top">Set Yelp Categories {!hideEditButtons &&
      <button className="btn btn-outline-secondary" onClick={() => setActiveTab("Yelp")}><i
        className="fas fa-edit"/> Edit</button>}</h5>
      <div className="row card-spacer-top">
        {selectedYelpCategories.map(yelpCategory => {
          return <div className="col-12 col-sm-12">
            {yelpCategory}
          </div>
        })}
      </div>
    </div>}
    {(!!patch.yelpListingManagementSpecs && Array.isArray(patch.yelpListingManagementSpecs.images) && patch.yelpListingManagementSpecs.images.length > 0) &&
    <div>
      <h5 className="card-spacer-top">Add Yelp Images {!hideEditButtons &&
      <button className="btn btn-outline-secondary" onClick={() => setActiveTab("Yelp")}><i
        className="fas fa-edit"/> Edit</button>}</h5>
      <div className="row card-spacer-top">
        {patch.yelpListingManagementSpecs.images.map(yelpImage => {
          return <div className="col-4 col-sm-4">
            <div className="img-gallery-item">
              <div className="text-center overflow-hidden">
                <img src={yelpImage.url} className="content-spacer-top" style={{"height": "100px"}}/>
                <div>{yelpImage.caption || ""}</div>
              </div>
            </div>
          </div>
        })}
      </div>
    </div>}
    {!!patch.yelpListingManagementSpecs && !!patch.yelpListingManagementSpecs.covidBannerMessage && <div>
      <h5 className="card-spacer-top">COVID-19 Banner Message {!hideEditButtons &&
      <button className="btn btn-outline-secondary" onClick={() => setActiveTab("Yelp")}><i
        className="fas fa-edit"/> Edit</button>}</h5>
      <div className="row card-spacer-top">
        <div className="col-12 col-sm-12">
          {patch.yelpListingManagementSpecs.covidBannerMessage}
        </div>
      </div>
    </div>}
    {!!patch.facebookListingManagementSpecs && !!patch.facebookListingManagementSpecs.about && <div>
      <h5 className="card-spacer-top">Facebook About {!hideEditButtons &&
      <button className="btn btn-outline-secondary" onClick={() => setActiveTab("Facebook")}><i
        className="fas fa-edit"/> Edit</button>}</h5>
      <div className="row card-spacer-top">
        <div className="col-12 col-sm-12">
          {patch.facebookListingManagementSpecs.about}
        </div>
      </div>
    </div>}
    {!!patch.listingManagementSpecs && !!(patch.listingManagementSpecs.providerTrackingNumbers || {}).hasOwnProperty("FACEBOOK") && <div>
      <h5 className="card-spacer-top">Facebook Call Tracking Number {!hideEditButtons &&
      <button className="btn btn-outline-secondary" onClick={() => setActiveTab("Facebook")}><i
        className="fas fa-edit"/> Edit</button>}</h5>
      <div className="row card-spacer-top">
        <div className="col-12 col-sm-12">
          {patch.listingManagementSpecs.providerTrackingNumbers.FACEBOOK}
        </div>
      </div>
    </div>}
    {!!patch.listingManagementSpecs && !!(patch.listingManagementSpecs.providerTrackingWebsiteUrls || {}).hasOwnProperty("FACEBOOK") &&
    <div>
      <h5 className="card-spacer-top">Facebook UTM/Tracking Website URL {!hideEditButtons &&
      <button className="btn btn-outline-secondary" onClick={() => setActiveTab("Facebook")}><i
        className="fas fa-edit"/> Edit</button>}</h5>
      <div className="row card-spacer-top">
        <div className="col-12 col-sm-12">
          {patch.listingManagementSpecs.providerTrackingWebsiteUrls.FACEBOOK}
        </div>
      </div>
    </div>}
    {!!patch.facebookListingManagementSpecs && !!patch.facebookListingManagementSpecs.coverPhotoUrl && <div>
      <h5 className="card-spacer-top">Set Facebook Cover Photo {!hideEditButtons &&
      <button className="btn btn-outline-secondary" onClick={() => setActiveTab("Facebook")}><i
        className="fas fa-edit"/> Edit</button>}</h5>
      <div className="row card-spacer-top">
        <div className="col-4 col-sm-4">
          <div className="img-gallery-item">
            <div className="text-center overflow-hidden">
              <img src={patch.facebookListingManagementSpecs.coverPhotoUrl} className="content-spacer-top"
                   style={{"height": "100px"}}/>
            </div>
          </div>
        </div>
      </div>
    </div>}
    {!!appointmentUrl && <div>
      <h5 className="card-spacer-top">Appointment URL {!hideEditButtons &&
      <button className="btn btn-outline-secondary" onClick={() => setActiveTab("General")}><i
        className="fas fa-edit"/> Edit</button>}</h5>
      <div className="row card-spacer-top">
        <div className="col-12 col-sm-12">
          {appointmentUrl}
        </div>
      </div>
    </div>}
    {!!patch.listingManagementSpecs && !!(patch.listingManagementSpecs.providerTrackingNumbers || {}).hasOwnProperty("GOOGLEMAP") && <div>
      <h5 className="card-spacer-top">Google Call Tracking Number {!hideEditButtons &&
      <button className="btn btn-outline-secondary" onClick={() => setActiveTab("Bing")}><i
        className="fas fa-edit"/> Edit</button>}</h5>
      <div className="row card-spacer-top">
        <div className="col-12 col-sm-12">
          {patch.listingManagementSpecs.providerTrackingNumbers.GOOGLEMAP}
        </div>
      </div>
    </div>}
    {!!googleTrackingUrl && <div>
      <h5 className="card-spacer-top">Google UTM/Tracking Website URL {!hideEditButtons &&
      <button className="btn btn-outline-secondary" onClick={() => setActiveTab("Google")}><i
        className="fas fa-edit"/> Edit</button>}</h5>
      <div className="row card-spacer-top">
        <div className="col-12 col-sm-12">
          {patch.listingManagementSpecs.providerTrackingWebsiteUrls.GOOGLEMAP}
        </div>
      </div>
    </div>}
    {(!!googleCategories) && <div>
      <h5 className="card-spacer-top">Additional Google Categories {!hideEditButtons &&
      <button className="btn btn-outline-secondary" onClick={() => setActiveTab("Google")}><i
        className="fas fa-edit"/> Edit</button>}</h5>
      <div className="row card-spacer-top">
        {googleCategories.map(googleCategory => {
          return <div className="col-12 col-sm-12">
            {googleCategory}
          </div>
        })}
      </div>
    </div>}
    {(!!googleAttributes && googleAttributes.length > 0) && <div>
      <h5 className="card-spacer-top"> Google Attributes {!hideEditButtons &&
      <button className="btn btn-outline-secondary" onClick={() => setActiveTab("Google")}><i
          className="fas fa-edit"/> Edit</button>}</h5>
      <div className="row card-spacer-top">
        {googleAttributes.map(googleAttribute => {
          return <div className="col-12 col-sm-12" key={googleAttribute.attributeId}>
            {attributeMods(googleAttribute)}
          </div>
        })}
      </div>
    </div>}
    {googleManuallyPlacedPin && <div>
      <h5 className="card-spacer-top"> Manually Placed Pin - Do Not Move {!hideEditButtons &&
      <button className="btn btn-outline-secondary" onClick={() => setActiveTab("Google")}><i
        className="fas fa-edit"/> Edit</button>}</h5>
      <div className="row card-spacer-top">
        <div className="col-12 col-sm-12">
          {patch.googleListingManagementSpecs.doNotMovePin ? "Yes" : "No"}
        </div>
      </div>
    </div>}
    {googleCoverPhotos.length > 0 && <div>
      <h5 className="card-spacer-top">Set Google Cover Photo {!hideEditButtons &&
      <button className="btn btn-outline-secondary" onClick={() => setActiveTab("Google")}><i
        className="fas fa-edit"/> Edit</button>}</h5>
      <div className="row card-spacer-top">
        <div className="col-4 col-sm-4">
          <div className="img-gallery-item">
            <div className="text-center overflow-hidden">
              <img src={googleCoverPhotos[0].sourceUrl} className="content-spacer-top" style={{"height": "100px"}}/>
            </div>
          </div>
        </div>
      </div>
    </div>}
    {googleProfilePhotos.length > 0 && <div>
      <h5 className="card-spacer-top">Set Google Profile Photo {!hideEditButtons &&
      <button className="btn btn-outline-secondary" onClick={() => setActiveTab("Google")}><i
        className="fas fa-edit"/> Edit</button>}</h5>
      <div className="row card-spacer-top">
        <div className="col-4 col-sm-4">
          <div className="img-gallery-item">
            <div className="text-center overflow-hidden">
              <img src={googleProfilePhotos[0].sourceUrl} className="content-spacer-top" style={{"height": "100px"}}/>
            </div>
          </div>
        </div>
      </div>
    </div>}
    {(googleAdditionalPhotos.length > 0) && <div>
      <h5 className="card-spacer-top">Additional Google Images {!hideEditButtons &&
      <button className="btn btn-outline-secondary" onClick={() => setActiveTab("Google")}><i
        className="fas fa-edit"/> Edit</button>}</h5>
      <div className="row card-spacer-top">
        {googleAdditionalPhotos.map(googleImage => {
          return <div className="col-4 col-sm-4">
            <div className="img-gallery-item">
              <div className="text-center overflow-hidden">
                <img src={googleImage.sourceUrl  || googleImage.googleUrl} className="content-spacer-top" style={{"height": "100px"}}/>
              </div>
            </div>
          </div>
        })}
      </div>
    </div>}

    {(googleRemovedPhotos.length > 0) && <div>
      <h5 className="card-spacer-top">Removed Google Images {!hideEditButtons &&
      <button className="btn btn-outline-secondary" onClick={() => setActiveTab("Google")}><i
          className="fas fa-edit"/> Edit</button>}</h5>
      <div className="row card-spacer-top">
        {googleRemovedPhotos.map(googleImage => {
          return <div className="col-4 col-sm-4">
            <div className="img-gallery-item">
              <div className="text-center overflow-hidden">
                <img src={googleImage.sourceUrl || googleImage.googleUrl} className="content-spacer-top" style={{"height": "100px"}}/>
              </div>
            </div>
          </div>
        })}
      </div>
    </div>}

    {!!maybePrimaryAppleCategory && <div>
      <h5 className="card-spacer-top">Set Apple Category {!hideEditButtons &&
      <button className="btn btn-outline-secondary" onClick={() => setActiveTab("Apple")}><i
        className="fas fa-edit"/> Edit</button>}</h5>
      <div className="row card-spacer-top">
        <div className="col-12 col-sm-12">
          {maybePrimaryAppleCategory.text}
        </div>
      </div>
    </div>}
    {(selectedAppleCategories.length > 0) && <div>
      <h5 className="card-spacer-top">Set Additional Apple Categories {!hideEditButtons &&
      <button className="btn btn-outline-secondary" onClick={() => setActiveTab("Apple")}><i
        className="fas fa-edit"/> Edit</button>}</h5>
      <div className="row card-spacer-top">
        {selectedAppleCategories.map(appleCategory => {
          return <div className="col-12 col-sm-12">
            {appleCategory}
          </div>
        })}
      </div>
    </div>}

    {(keywords.length > 0) && <div>
      <h5 className="card-spacer-top">Keywords {!hideEditButtons &&
      <button className="btn btn-outline-secondary" onClick={() => setActiveTab("Keywords")}><i
        className="fas fa-edit"/> Edit</button>}</h5>
      <div className="row card-spacer-top">
        {keywords.map(keywordRecord => {
          return <div className="col-12 col-sm-12">
            {keywordRecord.keyword} - {keywordRecord.localArea}
          </div>
        })}
      </div>
    </div>}


    {!!maybePrimaryBingCategory && <div>
      <h5 className="card-spacer-top">Set Bing Category {!hideEditButtons &&
      <button className="btn btn-outline-secondary" onClick={() => setActiveTab("Bing")}><i
        className="fas fa-edit"/> Edit</button>}</h5>
      <div className="row card-spacer-top">
        <div className="col-12 col-sm-12">
          {maybePrimaryBingCategory.text}
        </div>
      </div>
    </div>}
    {!hasRequiredPrimaryBingCategory && <div>
      <h5 className="card-spacer-top">Set Bing Category {!hideEditButtons &&
      <button className="btn btn-outline-secondary" onClick={() => setActiveTab("Bing")}><i
        className="fas fa-edit"/> Edit</button>}</h5>
      <div className="row card-spacer-top">
        <div className="col-12 col-sm-12">
          <div className="invalid-feedback" style={{"display": "block"}}>Listing Management locations must specify a primary Bing category.</div>
        </div>
      </div>
    </div>}
    {(selectedBingCategories.length > 0) && <div>
      <h5 className="card-spacer-top">Set Additional Bing Categories {!hideEditButtons &&
      <button className="btn btn-outline-secondary" onClick={() => setActiveTab("Bing")}><i
        className="fas fa-edit"/> Edit</button>}</h5>
      <div className="row card-spacer-top">
        {selectedBingCategories.map(bingCategory => {
          return <div className="col-12 col-sm-12">
            {bingCategory}
          </div>
        })}
      </div>
    </div>}
    {!!patch.listingManagementSpecs && !!(patch.listingManagementSpecs.providerTrackingNumbers || {}).hasOwnProperty("BING") && <div>
      <h5 className="card-spacer-top">Bing Call Tracking Number {!hideEditButtons &&
      <button className="btn btn-outline-secondary" onClick={() => setActiveTab("Bing")}><i
        className="fas fa-edit"/> Edit</button>}</h5>
      <div className="row card-spacer-top">
        <div className="col-12 col-sm-12">
          {patch.listingManagementSpecs.providerTrackingNumbers.BING}
        </div>
      </div>
    </div>}
    {!!patch.listingManagementSpecs && !!(patch.listingManagementSpecs.providerTrackingWebsiteUrls || {}).hasOwnProperty("BING") && <div>
      <h5 className="card-spacer-top">Bing UTM/Tracking Website URL {!hideEditButtons &&
      <button className="btn btn-outline-secondary" onClick={() => setActiveTab("Bing")}><i
        className="fas fa-edit"/> Edit</button>}</h5>
      <div className="row card-spacer-top">
        <div className="col-12 col-sm-12">
          {patch.listingManagementSpecs.providerTrackingWebsiteUrls.BING}
        </div>
      </div>
    </div>}

    {!!foodMenuSummary && <div>
      <h5 className="card-spacer-top">Menus {!hideEditButtons &&
      <button className="btn btn-outline-secondary" onClick={() => setActiveTab("Menus")}><i
          className="fas fa-edit"/> Edit</button>}</h5>
      <table className="card-spacer-top">
        <tbody>
        <tr>
          <td>Name</td>
          <td>{foodMenuSummary.name}</td>
        </tr>
        <tr>
          <td>Sections</td>
          <td>{foodMenuSummary.sectionCount}</td>
        </tr>
        <tr>
          <td>Items</td>
          <td>{foodMenuSummary.itemCount}</td>
        </tr>
        </tbody>
      </table>
    </div>}
    {(!isOptionalArrayFieldEmpty(startingLocationPatch.listingManagementSpecs.foodMenuIds) && isOptionalArrayFieldEmpty(locationPatch.listingManagementSpecs.foodMenuIds)) && <div>
      <h5>Menus</h5>
      <div className="card-spacer-top">Menu Removed</div>
    </div>}
  </div>
}

export default BulkLocationEditorSummary
